import type { OrganizationLong } from 'src/api/main'
import { stringToHslColor } from 'src/utils'

/** Build deterministic two-letter initials from a workspace name. */
/**
 * Marks the sidebar's workspace tile so the one-time welcome can measure it and
 * fly its own tile into that exact spot.
 */
export const WORKSPACE_TILE_ID = 'shell-workspace-tile'

export const workspaceInitials = (name: string | null | undefined): string => {
  if (!name) {
    return 'W'
  }
  const parts = name
    .split(/[\s_-]+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .slice(0, 2)
  if (parts.length === 0) {
    return name.slice(0, 2).toUpperCase()
  }
  return parts
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
    .padEnd(1, 'W')
}

/** Tile colour when there is no name to derive one from yet. */
const FALLBACK_ACCENT = '#0F766E'

/**
 * Colour of a workspace tile that has no picture — the same derivation
 * `AvatarWithInitials` applies to a bare name, so the sidebar tile, the
 * overview hero and the onboarding preview all paint the same workspace the
 * same colour. Deliberately keyed by name, not id: the onboarding preview
 * promises the tile before an id exists.
 */
export const workspaceAccent = (name: string | null | undefined): string =>
  name?.trim() ? stringToHslColor(name) : FALLBACK_ACCENT

export const userInitials = (
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null
): string => {
  const f = firstName?.trim()?.[0]
  const l = lastName?.trim()?.[0]
  if (f && l) {
    return `${f}${l}`.toUpperCase()
  }
  if (f) {
    return f.toUpperCase()
  }
  if (email) {
    return email.slice(0, 2).toUpperCase()
  }
  return 'U'
}

export const formatWorkspaceLabel = (
  workspace: OrganizationLong | undefined
): string => {
  if (!workspace) {
    return ''
  }
  return workspace.name ?? workspace.domain_name ?? String(workspace.id)
}

export const resolveActiveWorkspace = (
  workspaces: OrganizationLong[],
  activeWorkspaceId: number | undefined
): OrganizationLong | null =>
  workspaces.find((w) => w.id === activeWorkspaceId) ?? workspaces[0] ?? null
